import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

function BroadcastStatus({ rows, t, onRetry }) {
  if (!rows.length) return null;
  const tone = (s) => (s === "done" ? "bg-green-100 text-green-700" : s === "interrupted" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800");
  return (
    <ul className="space-y-1" data-testid="broadcast-status">
      {rows.map((b) => (
        <li key={b.id} className="text-xs text-espresso/70 bg-white rounded-lg border border-app-border px-3 py-2 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-espresso truncate flex-1 min-w-[160px]">{b.subject}{b.retry_of ? ` (${t("bcast.retry_label")})` : ""}</span>
            <span>{new Date(b.started_at).toLocaleString()}</span>
            <span className={`px-2 py-0.5 rounded-full font-bold ${tone(b.status)}`} data-testid={`bcast-row-status-${b.id}`}>
              {t(`bcast.${b.status}`)} · {b.sent}/{b.total}{b.failed ? ` · ${b.failed} ${t("bcast.failed")}` : ""}
            </span>
            {(b.failed > 0 || b.status === "interrupted") && b.status !== "running" && (
              <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => onRetry(b.id)} data-testid={`bcast-retry-${b.id}`}>
                {t("bcast.retry")}
              </Button>
            )}
          </div>
          {b.last_error && <div className="text-red-700" data-testid="bcast-last-error">{t("bcast.last_error")}: {b.last_error}</div>}
        </li>
      ))}
    </ul>
  );
}

function HealthBanner({ h, t }) {
  if (!h) return null;
  return (
    <div
      className={`rounded-lg px-3 py-2 text-xs flex flex-wrap items-center gap-2 border ${h.ok ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}
      data-testid="bcast-health"
    >
      {h.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      <span className="font-semibold">{h.ok ? t("bcast.health_ok") : t("bcast.health_bad")}</span>
      <span className="opacity-80">{h.from}</span>
      {!h.ok && <span className="w-full sm:w-auto">{h.error}</span>}
    </div>
  );
}

export const EmailBroadcast = () => {
  const { t } = useI18n();
  const [form, setForm] = useState(null);
  const [recipients, setRecipients] = useState(0);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [rows, setRows] = useState([]);
  const [health, setHealth] = useState(null);

  const loadStatus = () => api.get("/admin/email-broadcast/status").then(({ data }) => setRows(data.broadcasts)).catch(() => {});
  const loadHealth = () => api.get("/admin/email-broadcast/health").then(({ data }) => setHealth(data)).catch(() => setHealth({ ok: false, error: "health check failed" }));

  useEffect(() => {
    api.get("/admin/email-broadcast/template").then(({ data }) => {
      setForm({ subject: data.subject, body_ar: data.body_ar, body_en: data.body_en });
      setRecipients(data.recipients);
    });
    loadStatus();
    loadHealth();
  }, []);

  useEffect(() => {
    if (!rows.some((r) => r.status === "running" || r.status === "queued")) return;
    const id = setInterval(loadStatus, 2000);
    return () => clearInterval(id);
  }, [rows]);

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const sendTest = async () => {
    setBusy(true);
    try {
      await api.post("/admin/email-broadcast/test", { ...form, test_to: testTo });
      toast.success(t("bcast.test_sent"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("share.failed"));
    }
    setBusy(false);
  };

  const sendAll = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      await api.post("/admin/email-broadcast/send", form);
      toast.success(t("bcast.started"));
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("share.failed"));
    }
    setBusy(false);
  };

  const retry = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/email-broadcast/${id}/retry-failed`);
      toast.success(t("bcast.started"));
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("share.failed"));
    }
    setBusy(false);
  };

  return (
    <section className="space-y-3" data-testid="email-broadcast">
      <div>
        <h2 className="font-heading text-xl font-semibold text-espresso flex items-center gap-2"><Mail className="w-5 h-5 text-olive" />{t("bcast.title")}</h2>
        <p className="text-xs text-espresso/60">{t("bcast.sub").replace("{n}", recipients)}</p>
      </div>
      <HealthBanner h={health} t={t} />
      <div className="bg-white rounded-xl border border-app-border p-4 space-y-3">
        <Input value={form.subject} onChange={set("subject")} placeholder={t("bcast.subject")} data-testid="bcast-subject" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Textarea dir="rtl" rows={6} value={form.body_ar} onChange={set("body_ar")} placeholder="العربية" data-testid="bcast-body-ar" />
          <Textarea dir="ltr" rows={6} value={form.body_en} onChange={set("body_en")} placeholder="English" data-testid="bcast-body-en" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder={t("bcast.test_placeholder")} className="max-w-xs" data-testid="bcast-test-email" />
          <Button variant="outline" size="sm" onClick={sendTest} disabled={busy || !testTo} data-testid="bcast-send-test">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />}{t("bcast.send_test")}
          </Button>
          <Button size="sm" onClick={() => setConfirm(true)} disabled={busy} className="ml-auto rtl:ml-0 rtl:mr-auto" data-testid="bcast-send-all">
            <Send className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />{t("bcast.send_all").replace("{n}", recipients)}
          </Button>
        </div>
      </div>
      <BroadcastStatus rows={rows} t={t} onRetry={retry} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="bcast-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("bcast.confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("bcast.confirm_body").replace("{n}", recipients)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bcast-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={sendAll} data-testid="bcast-confirm-send">{t("bcast.confirm_yes")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
